import { useState } from "react";
import { Form, required, useNotify, useTranslate } from "ra-core";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";

import { getSupabaseClient } from "../providers/supabase/supabase";

type MfaTotpEnrollProps = {
  onEnrolled: (factorId: string) => void;
};

export const MfaTotpEnroll = ({ onEnrolled }: MfaTotpEnrollProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState(false);

  const startEnroll = async () => {
    setEnrolling(true);
    setLoading(true);
    try {
      const { data, error } = await getSupabaseClient().auth.mfa.enroll({
        factorType: "totp",
        friendlyName: translate("crm.mfa.totp.friendly_name", {
          _: "Authenticator app",
        }),
      });

      if (error) {
        throw error;
      }

      setFactorId(data.id);
      setQrCode(data.totp?.qr_code ?? null);
      setSecret(data.totp?.secret ?? null);
    } catch (error: any) {
      notify(error?.message ?? "crm.mfa.enroll_error", {
        type: "error",
        messageArgs: {
          _: "Could not start authenticator setup. Please try again.",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify: SubmitHandler<FieldValues> = async (values) => {
    if (!factorId) return;

    setLoading(true);
    try {
      const { data: challenge, error: challengeError } =
        await getSupabaseClient().auth.mfa.challenge({ factorId });

      if (challengeError) {
        throw challengeError;
      }

      const { error: verifyError } = await getSupabaseClient().auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: values.code,
      });

      if (verifyError) {
        throw verifyError;
      }

      onEnrolled(factorId);
    } catch (error: any) {
      notify(error?.message ?? "crm.mfa.verify_error", {
        type: "error",
        messageArgs: {
          _: "Invalid code. Please try again.",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  if (!enrolling) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {translate("crm.mfa.totp.description", {
            _: "Use Microsoft Authenticator, Google Authenticator, or another TOTP app.",
          })}
        </p>
        <Button type="button" onClick={startEnroll} disabled={loading}>
          {translate("crm.mfa.totp.start", { _: "Set up authenticator app" })}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {qrCode ? (
        <div
          className="mx-auto w-fit rounded-md border bg-white p-4"
          dangerouslySetInnerHTML={{ __html: qrCode }}
        />
      ) : null}
      {secret ? (
        <p className="text-sm break-all text-muted-foreground">
          {translate("crm.mfa.totp.manual_secret", { _: "Manual entry key:" })}{" "}
          <code className="font-mono text-foreground">{secret}</code>
        </p>
      ) : null}
      <p className="text-sm text-muted-foreground">
        {translate("crm.mfa.totp.scan_hint", {
          _: "Scan the QR code, then enter the 6-digit code from your app.",
        })}
      </p>
      <Form onSubmit={handleVerify}>
        <TextInput
          source="code"
          label="crm.mfa.code"
          validate={required()}
          helperText={false}
          autoComplete="one-time-code"
        />
        <Button type="submit" className="w-full mt-4" disabled={loading}>
          {translate("crm.mfa.verify_and_continue", {
            _: "Verify and continue",
          })}
        </Button>
      </Form>
    </div>
  );
};
